/**
 * Cohesive ChatKit — activity rail: search / thinking / tool-call / file steps
 * hang off a 24px glyph gutter. The connector is a full-height pseudo-line;
 * the opaque glyph (bg-card) masks it where each step sits.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx (type-scale
 * translation applied). ToolCallBlock accepts arbitrary children so product
 * renderers (forms, terminal output) can live inside the disclosure.
 */
import {
  CheckCircle2,
  ChevronDown,
  File as FileIcon,
  FileText,
  Globe,
  type LucideIcon,
  Presentation,
} from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Markdown } from "../Markdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Favicon } from "./atoms";

export function RailStep({
  glyph: Glyph,
  icon,
  tone = "default",
  last = false,
  children,
  className,
}: {
  glyph?: LucideIcon;
  /** Arbitrary glyph content (e.g. a platform logo) instead of a Lucide icon. */
  icon?: React.ReactNode;
  tone?: "default" | "done" | "tinted";
  last?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative grid grid-cols-[24px_1fr] gap-3",
        last ? "pb-0" : "pb-4",
        !last &&
          "before:absolute before:bottom-[-2px] before:left-[11px] before:top-[26px] before:w-px before:bg-n-20",
        className,
      )}
    >
      <span
        className={cn(
          // Masks the connector line where each step sits — match the chat
          // surface (bg-background), not bg-card, or a lighter square shows
          // through in dark mode (card=n-10 vs background=n-05).
          "relative z-[1] inline-flex size-6 items-center justify-center bg-background",
          tone === "done" ? "text-lc-active" : "text-n-80",
          tone === "tinted" &&
            "rounded-sm bg-[color-mix(in_oklab,var(--rust)_14%,var(--background))] text-rust",
        )}
      >
        {Glyph ? <Glyph className="size-4" /> : icon}
      </span>
      <div className="min-w-0 pt-px">{children}</div>
    </div>
  );
}

export interface SearchResultItem {
  title: string;
  domain: string;
  url?: string;
}

function ResultRow({ result }: { result: SearchResultItem }) {
  const inner = (
    <>
      <Favicon domain={result.domain} />
      <span className="min-w-0 flex-1 truncate font-sans text-xs text-n-80">
        {result.title}
      </span>
      <span className="shrink-0 whitespace-nowrap font-sans text-xs text-n-50">
        {result.domain}
      </span>
    </>
  );
  const rowClass =
    "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150 ease-out hover:bg-n-05";
  return result.url ? (
    <a href={result.url} target="_blank" rel="noreferrer" className={rowClass}>
      {inner}
    </a>
  ) : (
    <div className={rowClass}>{inner}</div>
  );
}

/** A web search with its result rows. */
export function SearchStep({
  query,
  count,
  results,
  last,
}: {
  query: string;
  count?: number;
  results?: SearchResultItem[];
  last?: boolean;
}) {
  return (
    <RailStep glyph={Globe} last={last}>
      <div className="flex items-baseline gap-2 font-sans text-sm text-n-70">
        <span className="min-w-0 flex-1 truncate text-n-70">{query}</span>
        {count !== undefined && (
          <span className="shrink-0 whitespace-nowrap font-mono text-xs text-n-50">
            {count} result{count === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {results && results.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-md bg-card shadow-hairline [&>*+*]:border-t [&>*+*]:border-n-10">
          {results.map((r, i) => (
            <ResultRow key={i} result={r} />
          ))}
        </div>
      )}
    </RailStep>
  );
}

/**
 * Reasoning paragraphs behind the Clock glyph — collapsed by default. The
 * header label doubles as the toggle (e.g. a live "Thinking 3s" timer while the
 * model reasons, or the final "Thought for 12s"); clicking reveals the prose.
 */
export function ThinkingStep({
  paras,
  label,
  defaultOpen = false,
  last,
}: {
  paras: string[];
  /** Head line + toggle, e.g. "Thinking 3s" / "Thought for 12s". */
  label?: string;
  /** Expand on mount; otherwise the step shows only the label row. */
  defaultOpen?: boolean;
  last?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <RailStep
      icon={<span className="size-1.5 rounded-full bg-n-40" />}
      last={last}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="-ml-1 flex items-center gap-1 font-sans text-[13px] text-n-50 outline-none transition-colors duration-150 ease-out hover:text-n-70 focus-visible:text-n-70"
      >
        <span>{label ?? "Thoughts"}</span>
        <ChevronDown
          className={cn(
            "size-3 transition-transform duration-150",
            open && "rotate-180",
          )}
        />
      </button>
      {open && paras.length > 0 && (
        <Markdown className="mt-1.5 max-h-60 overflow-y-auto pr-1 font-sans text-xs text-n-50 text-pretty [&_p]:my-0 [&_p:not(:last-child)]:mb-2">
          {paras.join("\n\n")}
        </Markdown>
      )}
    </RailStep>
  );
}

export interface ToolCallRow {
  verb: string;
  name?: string;
  add?: number;
  del?: number;
}

/** Collapsible tool-call / activity disclosure. */
export function ToolCallBlock({
  summary,
  rows,
  defaultOpen = false,
  children,
  className,
}: {
  summary: React.ReactNode;
  rows?: ToolCallRow[];
  defaultOpen?: boolean;
  /** Arbitrary disclosure body (renderers, forms, terminal output). */
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? "tc" : undefined}
      className={cn("w-full", className)}
    >
      <AccordionItem value="tc" className="border-b-0">
        <AccordionTrigger className="min-h-0 justify-start gap-2 py-0 text-sm font-normal text-n-60 hover:no-underline [&>svg]:order-first [&>svg]:size-4">
          {summary}
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          {rows && rows.length > 0 && (
            <div className="mt-1 rounded-md bg-[color-mix(in_oklab,var(--n-10)_45%,var(--card))] p-1">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-sm px-2.5 py-1.5 font-sans text-xs text-n-60 transition-colors hover:bg-card"
                >
                  <span className="text-n-50">{r.verb}</span>
                  {r.name && (
                    <span className="font-mono text-xs text-n-90">
                      {r.name}
                    </span>
                  )}
                  {r.add != null && (
                    <span className="font-mono text-xs text-spectrum-green">
                      +{r.add}
                    </span>
                  )}
                  {r.del != null && (
                    <span className="font-mono text-xs text-spectrum-red">
                      -{r.del}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {children && <div className="mt-1">{children}</div>}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

/** File-producing activity: created → presented → done timeline. */
export function FileTimeline({
  summary,
  file,
  presented = true,
  done = true,
  defaultOpen = true,
}: {
  summary: React.ReactNode;
  file: { title: string; name: string };
  presented?: boolean;
  done?: boolean;
  defaultOpen?: boolean;
}) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? "ft" : undefined}
      className="w-full"
    >
      <AccordionItem value="ft" className="border-b-0">
        <AccordionTrigger className="min-h-0 justify-start gap-2 py-0 text-sm font-normal text-n-60 hover:no-underline [&>svg]:order-first [&>svg]:size-4">
          {summary}
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          <div className="mt-1 flex flex-col">
            <RailStep glyph={FileText} last={!presented && !done}>
              <div className="font-sans text-sm text-n-70">{file.title}</div>
              <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-sm bg-n-10 px-2 py-1 font-mono text-xs text-n-80">
                <FileIcon className="size-3" /> {file.name}
              </span>
            </RailStep>
            {presented && (
              <RailStep glyph={Presentation} last={!done}>
                <div className="font-sans text-sm text-n-70">
                  Presented file to the Canvas
                </div>
              </RailStep>
            )}
            {done && (
              <RailStep glyph={CheckCircle2} tone="done" last>
                <div className="font-sans text-sm font-medium text-n-90">
                  Done
                </div>
              </RailStep>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
